/**
 * WebRTCService — Manages one RTCPeerConnection for voice with a single remote peer.
 *
 * Adapted from Dive Deep Dating's voice/video implementation.
 * In DrawLab we use a mesh topology where each user maintains N-1 instances
 * of this service (one per other peer in the voice chat).
 *
 * Voice-only — no video.
 *
 * Handles:
 * - Peer connection creation with ICE server configuration
 * - SDP offer/answer creation
 * - ICE candidate gathering and exchange
 * - Adding a shared local stream (managed at the MeshVoiceManager level)
 * - Clean teardown
 */

import 'webrtc-adapter';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

// Free STUN servers — handle ~85% of connections without TURN
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

// Cloudflare TURN credentials, minted server-side and merged with STUN.
// TURN relays media for the ~15% of peers behind symmetric NATs / strict
// firewalls / CGNAT (mobile) where direct P2P fails. In a mesh EVERY pairwise
// link must connect, so this matters more as the room grows. Falls back to
// STUN-only when TURN isn't configured/available.
let runtimeIceServers = null;

/**
 * Fetch fresh TURN credentials and merge them into the ICE server list.
 * Call once before opening peer connections (e.g. when joining voice).
 * Best-effort — on failure we stay STUN-only rather than block the call.
 */
export async function refreshIceServers() {
  try {
    const fn = httpsCallable(functions, 'getTurnCredentials');
    const { data } = await fn({});
    const cf = data?.iceServers;
    runtimeIceServers = cf ? [...ICE_SERVERS, ...(Array.isArray(cf) ? cf : [cf])] : null;
  } catch (e) {
    console.warn('[WebRTC] TURN fetch failed — STUN only:', e?.message);
    runtimeIceServers = null;
  }
}

// Audio constraints optimised for voice conversation (shared across peers)
export const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export class WebRTCService {
  constructor(remotePeerId) {
    this.remotePeerId = remotePeerId;
    this.peerConnection = null;
    this.remoteStream = null;

    // Callbacks — set by MeshVoiceManager
    this.onRemoteStream = null;       // (remotePeerId, stream)
    this.onIceCandidate = null;        // (remotePeerId, candidate)
    this.onConnectionStateChange = null; // (remotePeerId, state)
    this.onCallEnded = null;           // (remotePeerId, reason)
  }

  /**
   * Initialise the RTCPeerConnection with ICE servers and event handlers.
   */
  createPeerConnection() {
    if (this.peerConnection) {
      console.warn('[WebRTC] Peer connection already exists — tearing down first');
      this.teardown();
    }

    this.peerConnection = new RTCPeerConnection({ iceServers: runtimeIceServers || ICE_SERVERS });
    this.remoteStream = new MediaStream();

    // Local ICE candidate — needs to be sent to the remote peer via signalling
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.onIceCandidate) {
        this.onIceCandidate(this.remotePeerId, event.candidate.toJSON());
      }
    };

    // Remote track received — attach to remoteStream
    this.peerConnection.ontrack = (event) => {
      event.streams[0]?.getTracks().forEach((track) => {
        this.remoteStream.addTrack(track);
      });
      if (this.onRemoteStream) {
        this.onRemoteStream(this.remotePeerId, this.remoteStream);
      }
    };

    // Connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState;
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(this.remotePeerId, state);
      }

      if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        if (this.onCallEnded) {
          this.onCallEnded(this.remotePeerId, state);
        }
      }
    };

    return this.peerConnection;
  }

  /**
   * Add local stream tracks to the peer connection.
   * The stream is owned by MeshVoiceManager and shared across all peer connections.
   */
  addLocalStream(localStream) {
    if (!localStream || !this.peerConnection) {
      throw new Error('Local stream or peer connection not initialised');
    }

    localStream.getTracks().forEach((track) => {
      this.peerConnection.addTrack(track, localStream);
    });
  }

  /**
   * Create an SDP offer (initiator side).
   */
  async createOffer() {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialised');
    }

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    return { type: offer.type, sdp: offer.sdp };
  }

  /**
   * Create an SDP answer (receiver side).
   */
  async createAnswer(offer) {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialised');
    }

    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    return { type: answer.type, sdp: answer.sdp };
  }

  /**
   * Set the remote SDP answer (initiator side, after receiver responds).
   */
  async setRemoteAnswer(answer) {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialised');
    }

    // Guard: only set answer when we're waiting for one
    const state = this.peerConnection.signalingState;
    if (state !== 'have-local-offer') {
      return;
    }

    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
  }

  /**
   * Add a remote ICE candidate.
   */
  async addIceCandidate(candidate) {
    if (!this.peerConnection) return;
    try {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      // Non-fatal — some candidates arrive after connection is established
      console.warn('[WebRTC] Failed to add ICE candidate:', error.message);
    }
  }

  /**
   * Get the current connection state.
   */
  get connectionState() {
    return this.peerConnection?.connectionState || 'closed';
  }

  /**
   * Clean up — close peer connection, clear state.
   * Note: does NOT stop the shared local stream (that's owned by MeshVoiceManager).
   */
  teardown() {
    // Stop remote stream tracks
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => track.stop());
      this.remoteStream = null;
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.onicecandidate = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }
  }
}

export default WebRTCService;
